import { AppBar, Toolbar, Typography, Box, IconButton, Avatar, Tooltip, Menu, MenuItem, Divider, ListItemIcon } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import CheckIcon from '@mui/icons-material/Check';
import TranslateIcon from '@mui/icons-material/Translate';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItemText from '@mui/material/ListItemText';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { SUPPORTED_UI_LANGUAGES, LANGUAGE_META, normalizeUiLanguage } from '../../i18n/languages';
import BrandSelector from './BrandSelector';
import NotificationsBell from './NotificationsBell';
import CreditBalanceChip from './CreditBalanceChip';

const PAGE_TITLE_KEYS: Record<string, string> = {
  '/': 'header.title.dashboard',
  '/strategy': 'header.title.brandKnowledge',
  '/geo-strategy': 'header.title.geoStrategy',
  '/audits': 'header.title.websiteAudit',
  '/earned-media': 'header.title.earnedMedia',
  '/brand': 'header.title.aiVisibility',
  '/brand/narrative': 'header.title.brandImage',
  '/brand/authority': 'header.title.authority',
  '/brand/referral': 'header.title.recommendations',
  '/brand/comparison': 'header.title.comparison',
  '/social': 'header.title.socialAudit',
  '/competitors': 'header.title.competitors',
  '/tasks': 'header.title.tasks',
  '/news-watch': 'header.title.newsWatch',
  '/settings': 'header.title.settings',
};

export default function Header() {
  const { user, logout, setUiLanguage } = useAuth();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [langAnchorEl, setLangAnchorEl] = useState<HTMLElement | null>(null);
  const currentLang = normalizeUiLanguage(user?.settings?.uiLanguage || i18n.language);

  const closeAll = () => { setLangAnchorEl(null); setAnchorEl(null); };

  const initials = user?.name
    ? user.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  const pageTitle = PAGE_TITLE_KEYS[location.pathname] ? t(PAGE_TITLE_KEYS[location.pathname]) : 'Traaker';

  return (
    <AppBar position="sticky" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      <Toolbar>
        <BrandSelector />
        <Divider orientation="vertical" flexItem sx={{ mx: 2, my: 1 }} />
        <Typography variant="body1" fontWeight={500} sx={{ flexGrow: 1, color: 'text.secondary' }}>
          {pageTitle}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CreditBalanceChip />
          <NotificationsBell />
          <Tooltip title={user?.name || ''}>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.75rem' }}>
                {initials}
              </Avatar>
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user?.email}</Typography>
            </MenuItem>
            <Divider />
            {/* Language — opens a submenu anchored to this row. */}
            <MenuItem onClick={(e) => setLangAnchorEl(e.currentTarget)}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                <TranslateIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{t('header.language')}</ListItemText>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 1 }}>
                {LANGUAGE_META[currentLang].label}
              </Typography>
              <ChevronRightIcon fontSize="small" sx={{ color: 'action.active' }} />
            </MenuItem>
            <Divider />
            <MenuItem onClick={logout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              {t('header.logout')}
            </MenuItem>
          </Menu>

          {/* Language submenu */}
          <Menu
            anchorEl={langAnchorEl}
            open={Boolean(langAnchorEl)}
            onClose={() => setLangAnchorEl(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {SUPPORTED_UI_LANGUAGES.map((code) => (
              <MenuItem
                key={code}
                selected={currentLang === code}
                onClick={() => { setUiLanguage(code); closeAll(); }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {currentLang === code ? <CheckIcon fontSize="small" /> : <span style={{ fontSize: 16 }}>{LANGUAGE_META[code].flag}</span>}
                </ListItemIcon>
                {LANGUAGE_META[code].label}
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
